import React, { useCallback, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import { InternalApiDraftReferenceType, InternalApiNoteDraft } from '../api-internal';
import { useMe } from '../organisation/useMe';
import { CAN_USE_NOTE_DRAFT } from '../auth/permissions';
import { showFeedback } from '../feedback/feedbackReducer';
import { DraftDeletedPayload, useNoteDraftEditingSync } from './useNoteDraftEditingSync';

interface StatusChange {
  newStatus?: string;
  newNote?: string;
}

interface UseDraftsParams {
  referenceId: string | number;
  referenceType: InternalApiDraftReferenceType;
  drafts: InternalApiNoteDraft[] | undefined;
  deleteDraft: (id: number) => Promise<void>;
  draftsQueryKey: string;
}

export interface UseDraftsResult {
  isDraftEnabled: boolean;
  organisationId: number | undefined;
  visibleDrafts: InternalApiNoteDraft[] | undefined;
  loadedExistingDraft: InternalApiNoteDraft | null;
  draftToDelete: InternalApiNoteDraft | null;
  conflictDraft: InternalApiNoteDraft | null;
  editingDraftId: number | null;
  isTakingOver: boolean;
  pendingChange: StatusChange | undefined;
  isDraftActiveElsewhere: (draftId: number) => boolean;
  handleDraftIdChange: (draftId: number | null) => void;
  handleEditDraft: (draft: InternalApiNoteDraft) => void;
  handleDeleteDraft: (draft: InternalApiNoteDraft) => void;
  handleTakeover: () => Promise<void>;
  confirmDeleteDraft: () => Promise<void>;
  resetDraftState: () => void;
  setPendingChange: React.Dispatch<React.SetStateAction<StatusChange | undefined>>;
  setDraftToDelete: React.Dispatch<React.SetStateAction<InternalApiNoteDraft | null>>;
  setConflictDraft: React.Dispatch<React.SetStateAction<InternalApiNoteDraft | null>>;
  setLoadedExistingDraft: React.Dispatch<React.SetStateAction<InternalApiNoteDraft | null>>;
  setEditingDraftId: React.Dispatch<React.SetStateAction<number | null>>;
  stopEditing: () => void;
  broadcastDraftDeleted: (
    draftId: number,
    referenceId: string | number,
    referenceType: InternalApiDraftReferenceType
  ) => void;
}

export const useNoteDrafts = ({
  referenceId,
  referenceType,
  drafts,
  deleteDraft,
  draftsQueryKey,
}: UseDraftsParams): UseDraftsResult => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { permissions, currentOrganisation } = useMe();
  const isDraftEnabled = permissions.includes(CAN_USE_NOTE_DRAFT);

  const [loadedExistingDraft, setLoadedExistingDraft] = useState<InternalApiNoteDraft | null>(null);
  const [draftToDelete, setDraftToDelete] = useState<InternalApiNoteDraft | null>(null);
  const [conflictDraft, setConflictDraft] = useState<InternalApiNoteDraft | null>(null);
  const [editingDraftId, setEditingDraftId] = useState<number | null>(null);
  const [isTakingOver, setIsTakingOver] = useState(false);
  const [pendingChange, setPendingChange] = useState<StatusChange>();

  const handleTakeoverRequested = useCallback(() => {
    setLoadedExistingDraft(null);
    setEditingDraftId(null);
    setPendingChange(undefined);
    dispatch(showFeedback({ message: 'Editing moved to another tab', variant: 'info' }));
  }, [dispatch]);

  const handleDraftDeletedInAnotherSession = useCallback(
    async (payload: DraftDeletedPayload) => {
      if (payload.referenceId === referenceId && payload.referenceType === referenceType) {
        if (loadedExistingDraft && payload.draftId === loadedExistingDraft.id) {
          setLoadedExistingDraft(null);
          setEditingDraftId(null);
          setPendingChange(undefined);
          dispatch(showFeedback({ message: 'This draft was deleted in another session', variant: 'info' }));
        }
        await queryClient.invalidateQueries({ queryKey: [draftsQueryKey] });
      }
    },
    [referenceId, referenceType, loadedExistingDraft, queryClient, dispatch, draftsQueryKey]
  );

  const { isDraftActiveElsewhere, startEditing, stopEditing, requestTakeover, broadcastDraftDeleted } =
    useNoteDraftEditingSync({
      onDraftDeleted: handleDraftDeletedInAnotherSession,
      onTakeoverRequested: handleTakeoverRequested,
    });

  const visibleDrafts = useMemo(() => {
    if (!drafts || editingDraftId === null) return drafts;
    return drafts.filter((d) => d.id !== editingDraftId);
  }, [drafts, editingDraftId]);

  const handleDraftIdChange = useCallback(
    (draftId: number | null) => {
      if (editingDraftId && !draftId) {
        stopEditing();
      }
      setEditingDraftId(draftId);
      if (draftId && !loadedExistingDraft) {
        startEditing(draftId, referenceId);
      }
    },
    [referenceId, editingDraftId, loadedExistingDraft, startEditing, stopEditing]
  );

  const proceedToEditDraft = useCallback(
    (draft: InternalApiNoteDraft) => {
      setLoadedExistingDraft(draft);
      setEditingDraftId(draft.id);
      setPendingChange({ newNote: draft.content });
      startEditing(draft.id, referenceId);
    },
    [referenceId, startEditing]
  );

  const handleEditDraft = useCallback(
    (draft: InternalApiNoteDraft) => {
      if (isDraftActiveElsewhere(draft.id)) {
        setConflictDraft(draft);
      } else {
        proceedToEditDraft(draft);
      }
    },
    [isDraftActiveElsewhere, proceedToEditDraft]
  );

  const handleTakeover = useCallback(async () => {
    if (!conflictDraft) return;
    setIsTakingOver(true);
    try {
      const confirmed = await requestTakeover(conflictDraft.id);

      if (!confirmed) {
        dispatch(showFeedback({ message: 'Other session did not respond. Proceeding with editing.', variant: 'info' }));
      }

      proceedToEditDraft(conflictDraft);
      setConflictDraft(null);
    } finally {
      setIsTakingOver(false);
    }
  }, [conflictDraft, requestTakeover, proceedToEditDraft, dispatch]);

  const handleDeleteDraft = useCallback((draft: InternalApiNoteDraft) => {
    setDraftToDelete(draft);
  }, []);

  const confirmDeleteDraft = useCallback(async () => {
    if (!draftToDelete || !currentOrganisation?.id) return;
    await deleteDraft(draftToDelete.id);
    broadcastDraftDeleted(draftToDelete.id, referenceId, referenceType);
    setDraftToDelete(null);
  }, [draftToDelete, deleteDraft, referenceId, referenceType, broadcastDraftDeleted, currentOrganisation?.id]);

  const resetDraftState = useCallback(() => {
    setLoadedExistingDraft(null);
    setEditingDraftId(null);
    stopEditing();
  }, [stopEditing]);

  return {
    isDraftEnabled,
    organisationId: currentOrganisation?.id,
    visibleDrafts,
    loadedExistingDraft,
    draftToDelete,
    conflictDraft,
    editingDraftId,
    isTakingOver,
    pendingChange,
    isDraftActiveElsewhere,
    handleDraftIdChange,
    handleEditDraft,
    handleDeleteDraft,
    handleTakeover,
    confirmDeleteDraft,
    resetDraftState,
    setPendingChange,
    setDraftToDelete,
    setConflictDraft,
    setLoadedExistingDraft,
    setEditingDraftId,
    stopEditing,
    broadcastDraftDeleted,
  };
};
